import { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Loader2, Sparkles, CheckCircle2, Wand2, VoicemailIcon, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { analyzeCampaignCall, generateCampaignCallFix } from "../../services/crmServices/campaignService";
import { updateAiAgent } from "../../services/crmServices/aiAgentService";
import { ProposalModal } from "./PromptDiff";

const SEVERITY_STYLES = {
    high: "bg-red-100 text-red-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-blue-100 text-blue-700"
};

const AUDIO_QUALITY_STYLES = {
    good: "bg-emerald-100 text-emerald-700",
    poor: "bg-amber-100 text-amber-700",
    unintelligible: "bg-red-100 text-red-700",
    unknown: "bg-gray-100 text-gray-500"
};

// Shown inside the campaign call detail modal. Lets an admin run Claude
// analysis on a REAL call (not a controlled test call) — including an
// explicit read on call quality (voicemail the agent failed to spot,
// garbled/unintelligible audio) — then generate and apply a prompt fix
// straight to the live Retell agent used by this campaign.
export function CallAnalysisPanel({ call, agentId }) {
    const [analysis, setAnalysis] = useState(call?.aiAnalysis?.analyzedAt ? call.aiAnalysis : null);
    const [analyzing, setAnalyzing] = useState(false);
    const [selectedFindings, setSelectedFindings] = useState({});
    const [fixing, setFixing] = useState(false);
    const [proposal, setProposal] = useState(null);
    const [applying, setApplying] = useState(false);

    const canAnalyze = !!(call?.transcript && call.transcript.trim());

    const analyze = async () => {
        setAnalyzing(true);
        try {
            const res = await analyzeCampaignCall(call._id);
            if (res?.status === 200 && res.data?.aiAnalysis) {
                setAnalysis(res.data.aiAnalysis);
                setSelectedFindings({});
                setProposal(null);
                toast.success("Analysis complete");
            } else {
                toast.error(res?.data?.message || "Analysis failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Analysis failed");
        } finally {
            setAnalyzing(false);
        }
    };

    const generateFix = async () => {
        const indexes = Object.entries(selectedFindings).filter(([, v]) => v).map(([k]) => parseInt(k, 10));
        setFixing(true);
        try {
            const res = await generateCampaignCallFix(call._id, indexes.length > 0 ? indexes : undefined);
            if (res?.status === 200 && (res.data?.changes?.length > 0 || res.data?.toolChanges?.length > 0)) {
                setProposal(res.data);
            } else {
                toast.error(res?.data?.message || "Fix generation failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Fix generation failed");
        } finally {
            setFixing(false);
        }
    };

    const applyFix = async (changes, toolChanges, publish) => {
        if (publish && !window.confirm(
            "This will publish these prompt changes LIVE to the agent used by this campaign — they apply to the next call immediately. Continue?"
        )) {
            return;
        }
        setApplying(true);
        try {
            const payload = { toolChanges: toolChanges || [], source: "ai-fix", publish: !!publish };
            const statePrompts = {};
            (changes || []).forEach((c) => {
                if (c.target === "general") payload.prompt = c.proposedPrompt;
                else statePrompts[c.target] = c.proposedPrompt;
            });
            if (Object.keys(statePrompts).length > 0) payload.statePrompts = statePrompts;

            const res = await updateAiAgent(agentId, payload);
            if (res?.status === 200 && res.data) {
                toast.success(
                    res.data.published
                        ? "Applied — published live to the agent"
                        : "Applied — saved as a draft (not live yet, publish it from AI Agents)"
                );
                setProposal(null);
            } else {
                toast.error(res?.data?.message || "Failed to apply changes");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to apply changes");
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="border border-orange-100 rounded-lg bg-orange-50/30">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-orange-100/70">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-700">
                    <Sparkles className="h-3.5 w-3.5" /> AI Call Analysis
                </div>
                {!analysis && (
                    <Button
                        size="sm"
                        className="h-7 text-[11px] bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={analyze}
                        disabled={!canAnalyze || analyzing}
                        title={canAnalyze ? "Have Claude analyze this call" : "No transcript yet"}
                    >
                        {analyzing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                        {analyzing ? "Analyzing..." : "Analyze with AI"}
                    </Button>
                )}
                {analysis && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={analyze} disabled={analyzing}>
                        {analyzing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                        Re-analyze
                    </Button>
                )}
            </div>

            {!analysis && !analyzing && (
                <p className="text-[11px] text-gray-500 px-3 py-3">
                    {canAnalyze
                        ? "Claude will check whether this was a real conversation or bad audio/voicemail the agent missed, and suggest concrete prompt fixes."
                        : "This call has no transcript yet — nothing to analyze."}
                </p>
            )}

            {analysis && (
                <div className="p-3 space-y-3">
                    {/* Call quality read */}
                    {analysis.callQuality && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <Badge className={`text-[10px] h-5 ${AUDIO_QUALITY_STYLES[analysis.callQuality.audioQuality] || AUDIO_QUALITY_STYLES.unknown}`}>
                                Audio: {analysis.callQuality.audioQuality || "unknown"}
                            </Badge>
                            {analysis.callQuality.isLikelyVoicemail && (
                                <Badge className="text-[10px] h-5 bg-blue-100 text-blue-700 flex items-center gap-1">
                                    <VoicemailIcon className="h-3 w-3" /> Likely voicemail/machine
                                </Badge>
                            )}
                            {analysis.callQuality.voicemailMissedByAgent && (
                                <Badge className="text-[10px] h-5 bg-red-100 text-red-700 flex items-center gap-1">
                                    <WifiOff className="h-3 w-3" /> Agent didn't spot it
                                </Badge>
                            )}
                        </div>
                    )}
                    {analysis.callQuality?.note && (
                        <p className="text-[11px] text-gray-600 italic">{analysis.callQuality.note}</p>
                    )}

                    <p className="text-xs text-gray-700">{analysis.summary}</p>

                    {analysis.whatWorked?.length > 0 && (
                        <div className="text-[11px] text-emerald-700 space-y-0.5">
                            {analysis.whatWorked.map((w, i) => (
                                <div key={i} className="flex gap-1.5"><CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" /> {w}</div>
                            ))}
                        </div>
                    )}

                    {analysis.findings?.length > 0 ? (
                        <>
                            <div className="space-y-1.5">
                                {analysis.findings.map((f, i) => (
                                    <label key={i} className="flex gap-2 p-2 bg-white rounded-md cursor-pointer border border-transparent hover:border-orange-200">
                                        <Checkbox
                                            checked={selectedFindings[i] ?? true}
                                            onCheckedChange={(v) => setSelectedFindings((s) => ({ ...s, [i]: !!v }))}
                                            className="mt-0.5"
                                        />
                                        <div className="text-[11px] min-w-0">
                                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                                <Badge className={`${SEVERITY_STYLES[f.severity]} text-[9px] h-4 px-1`}>{f.severity}</Badge>
                                                {f.targetState && (
                                                    <Badge className={`text-[9px] h-4 px-1 ${f.targetState === "general" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                                                        {f.targetState === "general" ? "general prompt" : f.targetState}
                                                    </Badge>
                                                )}
                                                <span className="font-medium text-gray-800">{f.problem}</span>
                                            </div>
                                            {f.transcriptMoment && <p className="text-gray-500 italic">"{f.transcriptMoment}"</p>}
                                            <p className="text-gray-600 mt-0.5"><strong>Cause:</strong> {f.promptCause}</p>
                                            <p className="text-emerald-700 mt-0.5"><strong>Fix:</strong> {f.suggestedFix}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <Button
                                size="sm"
                                className="w-full h-8 text-[11px] bg-orange-500 hover:bg-orange-600 text-white"
                                onClick={generateFix}
                                disabled={fixing}
                            >
                                {fixing ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1.5" />}
                                {fixing ? "Claude is writing the fix..." : "Improve agent — generate fix for selected findings"}
                            </Button>
                        </>
                    ) : (
                        <p className="text-[11px] text-emerald-600">No prompt problems found — this call went well.</p>
                    )}
                </div>
            )}

            <ProposalModal
                proposal={proposal}
                agentId={agentId}
                onClose={() => setProposal(null)}
                applying={applying}
                applyLabel="Apply & publish to agent"
                onApply={(changes, toolChanges) => applyFix(changes, toolChanges, true)}
            />
        </div>
    );
}
